import React, { useState, useEffect, useMemo } from "react";
import i18n from "@/utils/i18n";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import TabGroup from "@/components/ui/tab";
import Table from "@/components/ui/table";
import ProgressBar from "@/components/ui/progress-bar";
import {
  ArrowRightLong,
  SquareRoundedCheck,
  AlertSquareRounded,
} from "@/components/icons";
import EnrollmentCertificateViewModal from "../components/EnrollmentCertificateViewModal";
import ExamResultDrawer from "../components/ExamResultDrawer";
import ConfirmModal from "@/components/ui/confirm-modal";
import type { FilterField, SortOption, TableColumn } from "@/types/component";
import type {
  ClientExam,
  ClientExamActionType,
} from "../types/client-exams.types";
import { clientExamsApi } from "../services/client-exams.api";
import styles from "./ClientExamsPage.module.scss";

const ACTION_LABELS: Record<ClientExamActionType, string> = {
  viewCertificate: i18n.t("clientExams.actions.viewCertificate"),
  retakeExam: i18n.t("clientExams.actions.retakeExam"),
  retakeTraining: i18n.t("clientExams.actions.retakeTraining"),
  startSurvey: i18n.t("clientExams.actions.startSurvey"),
};

/** UI sort value -> API sortBy, sortOrder */
function parseSortValue(value: string): {
  sortBy?: "dueDate" | "examName" | "relatedEducationName";
  sortOrder?: "asc" | "desc";
} {
  const [field, order] = value.split("_");
  const sortOrder = (order === "desc" ? "desc" : "asc") as "asc" | "desc";
  if (field === "examName" || field === "name") {
    return { sortBy: "examName", sortOrder };
  }
  if (field === "relatedEducationName") {
    return { sortBy: "relatedEducationName", sortOrder };
  }
  if (field === "dueDate" || field === "deadline") {
    return { sortBy: "dueDate", sortOrder };
  }
  return {};
}

const ClientExamsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortValue, setSortValue] = useState("updatedAt_desc");
  const [items, setItems] = useState<ClientExam[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);
  const [selectedExamForCertificate, setSelectedExamForCertificate] =
    useState<ClientExam | null>(null);
  const [isRetakeModalOpen, setIsRetakeModalOpen] = useState(false);
  const [selectedExamForRetake, setSelectedExamForRetake] =
    useState<ClientExam | null>(null);
  const [isExamResultDrawerOpen, setIsExamResultDrawerOpen] = useState(false);
  const [selectedExamForResult, setSelectedExamForResult] =
    useState<ClientExam | null>(null);
  const [educationOptions, setEducationOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const status: "ongoing" | "completed" =
    activeTab === 0 ? "ongoing" : "completed";

  // Eğitim listesi (filtre seçenekleri için)
  useEffect(() => {
    clientExamsApi
      .getMyExamEducations({ status })
      .then(({ items }) => {
        const opts = items
          .filter((e) => e.educationId)
          .map((e) => ({ label: e.educationName, value: e.educationId }));
        setEducationOptions(opts);
      })
      .catch(() => setEducationOptions([]));
  }, [status]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch exams from API
  useEffect(() => {
    setLoading(true);
    const { sortBy, sortOrder } = parseSortValue(sortValue);
    const educationId =
      typeof filterValues.relatedEducation === "string"
        ? filterValues.relatedEducation || undefined
        : undefined;

    clientExamsApi
      .getMyExams({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
        educationId: educationId || undefined,
        status,
        sortBy,
        sortOrder,
      })
      .then(({ items: data, count }) => {
        setItems(data);
        setTotalRecords(count);
      })
      .catch(() => {
        setItems([]);
        setTotalRecords(0);
      })
      .finally(() => setLoading(false));
  }, [
    currentPage,
    pageSize,
    debouncedSearch,
    status,
    sortValue,
    filterValues.relatedEducation,
  ]);

  const tabs = [
    { label: t("clientExams.tabs.ongoing"), content: null },
    { label: t("clientExams.tabs.completed"), content: null },
  ];

  const filterFields: FilterField[] = useMemo(
    () => [
      {
        name: "relatedEducation",
        label: t("clientExams.filters.relatedEducation"),
        type: "dropdown",
        placeholder: t("clientExams.filters.relatedEducationPlaceholder"),
        options: [
          { label: t("clientExams.filters.all"), value: "" },
          ...educationOptions,
        ],
      },
    ],
    [t, educationOptions],
  );

  const sortOptions: SortOption[] = [
    {
      label: t("clientExams.sort.examNameAsc"),
      value: "examName_asc",
    },
    {
      label: t("clientExams.sort.examNameDesc"),
      value: "examName_desc",
    },
    {
      label: t("clientExams.sort.deadlineNewest"),
      value: "dueDate_desc",
    },
    {
      label: t("clientExams.sort.deadlineOldest"),
      value: "dueDate_asc",
    },
  ];

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    setCurrentPage(1);
  };
  const handleFilterChange = (filters: Record<string, unknown>) => {
    setFilterValues(filters);
    setCurrentPage(1);
  };
  const handleSortChange = (value: string) => {
    setSortValue(value);
    setCurrentPage(1);
  };
  const handlePageChange = (page: number) => setCurrentPage(page);
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const goToExamDetail = (row: ClientExam) => {
    if (activeTab === 0) {
      navigate(`/dashboard/client-exams/take/${row.id}`, {
        state: {
          examName: row.examName,
          examAttemptStatuses: row.examAttemptStatuses,
          maxExamFailureCount: row.maxExamFailureCount,
        },
      });
    } else {
      // navigate(`/dashboard/education-catalog/detail?id=${row.id}`);
    }
  };

  const handleActionClick = (row: ClientExam, e: React.MouseEvent) => {
    e.stopPropagation();
    if (row.isCompleted && row.actionType) {
      if (row.actionType === "viewCertificate") {
        setSelectedExamForCertificate(row);
        setIsCertificateModalOpen(true);
      } else if (row.actionType === "retakeExam") {
        // Yoklama tam değilse retake da engellenmeli
        if (row.attendanceComplete === false) return;
        setSelectedExamForRetake(row);
        setIsRetakeModalOpen(true);
      } else {
        goToExamDetail(row);
      }
    } else {
      // Devam eden sınavlarda yoklama tam değilse sınava başlatma
      if (row.attendanceComplete === false) return;
      goToExamDetail(row);
    }
  };

  const handleCloseCertificateModal = () => {
    setIsCertificateModalOpen(false);
    setSelectedExamForCertificate(null);
  };

  const handleCloseRetakeModal = () => {
    setIsRetakeModalOpen(false);
    setSelectedExamForRetake(null);
  };

  const handleOpenExamResultDrawer = (row: ClientExam, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!row.attemptId) return;
    setSelectedExamForResult(row);
    setIsExamResultDrawerOpen(true);
  };

  const handleCloseExamResultDrawer = () => {
    setIsExamResultDrawerOpen(false);
    setSelectedExamForResult(null);
  };

  const handleConfirmRetake = () => {
    if (selectedExamForRetake) {
      handleCloseRetakeModal();
      goToExamDetail(selectedExamForRetake);
    }
  };

  const isOngoing = activeTab === 0;

  const ongoingColumns: TableColumn<ClientExam>[] = [
    {
      header: t("clientExams.table.examName"),
      accessor: (row) => row.examName,
    },
    {
      header: t("clientExams.table.relatedEducation"),
      accessor: (row) => row.relatedEducation,
    },
    {
      header: t("clientExams.table.passingGrade"),
      accessor: (row) => row.passingGrade,
    },
    {
      header: t("clientExams.table.failureLimit"),
      accessor: (row) =>
        row.maxExamFailureCount != null ? String(row.maxExamFailureCount) : "-",
    },
    {
      header: t("clientExams.table.deadline"),
      accessor: (row) => row.deadline,
    },
    {
      header: t("clientExams.table.progress"),
      accessor: (row) => (
        <div className={styles.progressCell}>
          <ProgressBar
            value={row.progressPercent ?? 0}
            showLabel={false}
            size="small"
          />
          <div className={styles.progressLabels}>
            <span className={styles.timeText}>
              {row.timeElapsed}/{row.timeTotal}
            </span>
            <span className={styles.fractionText}>{row.progressFraction}</span>
          </div>
        </div>
      ),
    },
    {
      header: "",
      accessor: (row) => {
        const attendanceLocked = row.attendanceComplete === false;
        return (
          <button
            type="button"
            className={styles.detailButton}
            onClick={(e) => {
              e.stopPropagation();
              if (attendanceLocked) return;
              goToExamDetail(row);
            }}
            disabled={attendanceLocked}
            title={attendanceLocked ? t("clientExams.attendanceLocked") : undefined}
            aria-label={
              attendanceLocked
                ? t("clientExams.attendanceLocked")
                : t("clientExams.continue")
            }
            style={attendanceLocked ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
          >
            <ArrowRightLong width={20} height={20} />
          </button>
        );
      },
    },
  ];

  const completedColumns: TableColumn<ClientExam>[] = [
    {
      header: t("clientExams.table.examName"),
      accessor: (row) => row.examName,
    },
    {
      header: t("clientExams.table.relatedEducation"),
      accessor: (row) => row.relatedEducation,
    },
    {
      header: t("clientExams.table.passingGrade"),
      accessor: (row) => row.passingGrade,
    },
    {
      header: t("clientExams.table.failureLimit"),
      accessor: (row) =>
        row.maxExamFailureCount != null ? String(row.maxExamFailureCount) : "-",
    },
    {
      header: t("clientExams.table.deadline"),
      accessor: (row) => row.deadline,
    },
    {
      header: t("clientExams.table.scoreReceived"),
      accessor: (row) => (
        <div className={styles.scoreCell}>
          <span
            className={
              row.passed === true
                ? styles.scorePass
                : row.passed === false
                  ? styles.scoreFail
                  : ""
            }
          >
            {row.scorePercentage != null
              ? t("clientExams.table.scorePercentage", {
                  value: Math.round(row.scorePercentage),
                })
              : row.score != null
                ? String(row.score)
                : "-"}
          </span>
          {row.passed === true ? (
            <span className={styles.scoreIconPass} aria-hidden>
              <SquareRoundedCheck width={22} height={22} />
            </span>
          ) : row.passed === false ? (
            <span className={styles.scoreIconFail} aria-hidden>
              <AlertSquareRounded width={22} height={22} />
            </span>
          ) : null}
        </div>
      ),
    },
    {
      header: "",
      accessor: (row) => (
        <div className={styles.actionsCell}>
          {row.isCompleted && row.attemptId ? (
            <button
              type="button"
              className={styles.actionTextBtn}
              onClick={(e) => handleOpenExamResultDrawer(row, e)}
              aria-label={t("clientExams.viewExamResult")}
            >
              {t("clientExams.viewExamResult")}
            </button>
          ) : null}
          {row.actionType ? (
            <button
              type="button"
              className={styles.actionTextBtn}
              onClick={(e) => handleActionClick(row, e)}
              aria-label={ACTION_LABELS[row.actionType]}
            >
              {ACTION_LABELS[row.actionType]}
            </button>
          ) : !row.attemptId ? (
            <button
              type="button"
              className={styles.detailButton}
              onClick={(e) => {
                e.stopPropagation();
                goToExamDetail(row);
              }}
              aria-label={t("clientExams.continue")}
            >
              <ArrowRightLong width={20} height={20} />
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  const tableColumns = isOngoing ? ongoingColumns : completedColumns;

  return (
    <DashboardLayout>
      <Header title={t("clientExams.title")} />

      <div className={styles.pageContainer}>
        <TabGroup
          tabs={tabs}
          defaultActiveTab={activeTab}
          onTabChange={handleTabChange}
          orientation="horizontal"
        />

        <PageHeader
          searchPlaceholder={t("clientExams.searchPlaceholder")}
          searchValue={searchTerm}
          onSearch={setSearchTerm}
          filters={filterFields}
          onFilterChange={handleFilterChange}
          showSort
          sortOptions={sortOptions}
          onSort={(option) => handleSortChange(option?.value ?? "updatedAt_desc")}
        />

        <div className={styles.tableWrap}>
          <Table
            columns={tableColumns}
            data={items}
            getRowId={(row) => row.id}
            onRowClick={goToExamDetail}
            currentPage={currentPage}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            hasVisibleActions={false}
            renderActions={() => null}
            contentBasedHeight
            isLoading={loading}
            emptyMessage={t("clientExams.noData")}
          />
        </div>
      </div>

      <ExamResultDrawer
        isOpen={isExamResultDrawerOpen}
        onClose={handleCloseExamResultDrawer}
        educationId={selectedExamForResult?.id ?? null}
        attemptId={selectedExamForResult?.attemptId ?? null}
        examName={selectedExamForResult?.examName}
      />

      <EnrollmentCertificateViewModal
        educationId={selectedExamForCertificate?.id}
        enrollmentId={selectedExamForCertificate?.enrollmentId}
        isOpen={isCertificateModalOpen}
        onClose={handleCloseCertificateModal}
      />

      <ConfirmModal
        isOpen={isRetakeModalOpen}
        onClose={handleCloseRetakeModal}
        onConfirm={handleConfirmRetake}
        title={
          selectedExamForRetake?.examName ??
          t("clientExams.retakeModal.title")
        }
        message={
          <>
            <strong>{selectedExamForRetake?.examName}</strong>{" "}
            {t("clientExams.retakeModal.message")}
          </>
        }
        confirmText={t("clientExams.retakeModal.confirm")}
        cancelText={t("clientExams.retakeModal.cancel")}
        examInfo={
          selectedExamForRetake
            ? {
                passingGrade: selectedExamForRetake.passingGrade,
                questionCount: 20, // TODO: Get from API
                duration: "30:00", // TODO: Get from API
                description: t(
                  "clientExams.retakeModal.description",
                  `Bu değerlendirme, yıllık zorunluluk kapsamında gerçekleştirilen '${selectedExamForRetake.relatedEducation}' eğitiminin son testidir. Sınav süresince kişisel koruyucu donanım kullanımı ve saha güvenliği protokolleri üzerine sorular yöneltilecektir.`,
                ),
              }
            : undefined
        }
      />
    </DashboardLayout>
  );
};

export default ClientExamsPage;
